import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CidaasService } from './../../../../service/cidaas.service';
import { SharedDataService } from './../../../../service/shared-data.service';
import { KEY_DEFAULT_ERROR } from '../../../../model/error.model';
import { isCidaasSystemUserId } from '../../../../model/domain/cidaas.model';
import { IInitiateMFAPayload } from '../../../../model/mfa.interface';

@Component({
  selector: 'app-initiate-authentication',
  templateUrl: './initiate-authentication.component.html',
  styleUrls: ['./initiate-authentication.component.scss'],
})
export class InitiateAuthenticationComponent implements OnInit, OnDestroy {
  @Input() showText: boolean = true;
  @Input() showInputField: boolean = true;
  @ViewChildren('li') liMFA: QueryList<any> | undefined;
  username: string = '';
  requestId: any = '';
  allowedMFA: string[] = [];
  usernameType: string = '';
  isInitiated: boolean = false;
  verifyInputOption = new BehaviorSubject({});
  usage_type: string = 'PASSWORDLESS_AUTHENTICATION';
  passwordless_enabled: boolean = false;
  authMethods: string[] = ['email', 'sms', 'totp', 'ivr', 'backupcode', 'touchid', 'push', 'pattern', 'fido2', 'face', 'password'];
  errorMessage: string = '';
  $composible = new Subscription();
  mediumList: any = [];
  displayedColumns: string[] = ['id', 'key_name', 'notification'];
  mfaItem: any;
  selectedVerificationType: string = '';
    inputOption = {
        emailCustom: {
            label: 'E-Mail',
            placeholder: 'E-Mail',
            hint: '',
            value: '',
            type: 'email',
            inputDisabled: true
        }
    };

  constructor(
    private route: ActivatedRoute,
    private _dataService: SharedDataService,
    private _cidaasSDK: CidaasService,
    private elementRef: ElementRef,
    private _router: Router,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // this._dataService._setRequestId(requestId);
    this._getUserHint();
    this._init();
  }

  _init = () => {
    this._getMFAList();

    this.inputOption.emailCustom.value = this.username;
  };

  async _assignMFAValues(response: any) {
    await this._dataService._getClientInfo().subscribe((res) => {
      this.allowedMFA = [];
      if (res['passwordless_enabled']) {
        response['configured_list'].map((item) => {
          this.allowedMFA.push(item.type.toLowerCase());
        });
        // fix(): Passwordless is disabled and password is coming under configured_list then show the password field.
      } else {
        if (
          response['configured_list'].filter((x) => {
            return x.type === 'PASSWORD';
          }).length
        ) {
          this.allowedMFA.push('password');
        }
      }
    });
  }

  _getUserHint = () => {
    let user_hint = this.route.snapshot.queryParamMap.get('userIdHint') ? this.route.snapshot.queryParamMap.get('userIdHint') : '';

    let usernameType = this.route.snapshot.queryParamMap.get('type') ? this.route.snapshot.queryParamMap.get('type') : '';

    this.username = user_hint ? user_hint : '';
    this.usernameType = usernameType ? usernameType : '';
  };

  _getRequestId = () => {
    this._dataService._getRequestId().subscribe((requestid) => {
      this.requestId = requestid;
    });
  };

  _getMFAList = () => {
    let options = {};
    this._dataService._getRequestId().subscribe((id) => {
      this.requestId = id;
      options['request_id'] = id;
      switch (this.usernameType) {
        case 'user_name': {
          options['username'] = this.username;
          break;
        }
        case 'mobile': {
          options['mobile_number'] = this.username;
          break;
        }
        default: {
          if (isCidaasSystemUserId(this.usernameType)) {
            options[this.usernameType] = this.username;
          } else {
            options['customFields'] = {
              [this.usernameType]: this.username
            };
          }
          break;
        }
      }
    });
    this._cidaasSDK
      ._getMFAList(options)
      .then((response) => {
        if (response && response.data) {
          this._dataService._setMFAList(response.data);
          this._assignMFAValues(response.data);
          setTimeout(() => {
            this.checkPasswordError();
          }, 0);
        } else if (response.error) {
          if (response.error.error === 'not able to find user sub') {
            this.errorMessage = 'NO_USER_FOUND';
          } else if (response.error.code === 10028) {
            this.errorMessage = 'IP_BLOCKED';
          } else {
            this.errorMessage = KEY_DEFAULT_ERROR;
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  _initiateAuthentication = (type: string, event, template?: TemplateRef<any>) => {
    this.selectedVerificationType = type;
    if (!event.target.classList.contains('active')) {
      // adding selection class to the mfa list
      this._changeSelection(event);
      this.errorMessage = '';

      const a = this._dataService._getMFAList().subscribe(async (data) => {
        if (data['configured_list'] != null && data['configured_list'].length > 0) {
          this.mfaItem = data['configured_list'].filter((mfa) => {
            return mfa.type.toLowerCase() === this.selectedVerificationType;
          });
          this.mediumList = [];
          if (this.authMethods.includes(this.selectedVerificationType)) {
            // check if multiple devices configured
            let selected_type: any = data['configured_list'].find(
              (c) => c.type.toLowerCase() == this.selectedVerificationType.toLowerCase()
            );
            if (selected_type.mediums && selected_type.mediums.length > 1) {
              this.mediumList = selected_type.mediums;
              if (template) {
                this.dialog.open(template);
              }
            } else {
              await this.triggerVerification(0);
            }
          } else {
            this._initiatePasswordField();
          }
        }
      });
      this.$composible.add(a);
    }
  };

  async triggerVerification(index: number) {
    const payload = this.setPayload(this.mfaItem, this.selectedVerificationType, index);
    const response = await this._cidaasSDK.initiateMFAV2(payload);
    this.showInputField = true;
    if (response.error) {
      this.errorMessage = KEY_DEFAULT_ERROR;
    }
    this.isInitiated = true;
    let options = this.setOptionsByType(this.selectedVerificationType);
    if (response && response.data) {
      this.setOptionsVerify(response, options);
      this.verifyInputOption.next(options);
    }
  }

  selectDevice(device): void {
    console.log(device);
    let index = this.mediumList.findIndex((c) => c.id == device.id);
    this.dialog.closeAll();
    this.triggerVerification(index);
  }

  _initiatePasswordField = () => {
    this.isInitiated = true;
    this.verifyInputOption.next({
      input: {
        label: 'LOGIN.AUTHENTICATION.PASSWORD.INPUT.LABEL',
        placeholder: 'LOGIN.AUTHENTICATION.PASSWORD.INPUT.PLACEHOLDER',
        hint: 'LOGIN.AUTHENTICATION.PASSWORD.INPUT.HINT',
        type: 'password',
        value: '',
      },
      button: {
        text: 'LOGIN.AUTHENTICATION.PASSWORD.BUTTON.TEXT',
        color: 'accent',
        showImg: false,
      },
      verify: {
        method: 'password',
      },
    });
  };

  _changeSelection = (event: any) => {
    this.liMFA?.forEach((liv) => {
      liv.nativeElement.firstChild.firstChild.classList.remove('active');
    });
    event.target.classList.add('active');
  };

  checkPasswordError() {
    let liMFALength = this.liMFA?.length || 2;
    this.liMFA?.forEach((liv) => {
      liv.nativeElement.classList.remove('active');
      if (liv.nativeElement.classList.contains('password')) {
        // liv.nativeElement.classList.add('active');
        liv.nativeElement.click();
      }
      if (liMFALength <= 1) {
        liv.nativeElement.click();
      }
    });
  }

  private setPayload(mfaItem: any, type: string, index: number): IInitiateMFAPayload {
    const payload: IInitiateMFAPayload = {};
    const medium_id = mfaItem[0]['mediums'] && mfaItem[0]['mediums'].length > 0 ? mfaItem[0]['mediums'][index]['id'] : mfaItem[0]['type'];
    payload['usage_type'] = this.usage_type;
    payload['request_id'] = this.requestId;
    payload['medium_id'] = medium_id;
    payload['type'] = type;
    if (isCidaasSystemUserId(this.usernameType)) {
      payload[this.usernameType == 'mobile' ? 'mobile_number' : this.usernameType == 'user_name' ? 'username' : this.usernameType] =
        this.username;
    } else {
      payload['customFields'] = {
        [this.usernameType]: this.username
      };
    }
    return payload;
  }

  private setOptionsVerify(response: any, options: any) {
    if(response && response.data ) {
      options.verify['exchange_id'] =
        response.data['exchange_id'] && response.data['exchange_id']['exchange_id'] ? response.data['exchange_id']['exchange_id'] : undefined;
      options.verify['sub'] = response.data['sub'] || undefined;
      options.verify['status_id'] = response.data['status_id'] || undefined;
      options.verify['push_selected_number'] = response.data['push_selected_number'] || undefined;
      options.verify['fido2_entity'] = response.data['fido2_entity'] || undefined;
    }
  }

  private setOptionsByType(type: string) {
    return {
      input: {
        label: ('login.authentication.' + type + '.input.label').toUpperCase(),
        placeholder: ('login.authentication.' + type + '.input.placeholder').toUpperCase(),
        hint: ('login.authentication.' + type + '.input.hint').toUpperCase(),
        type: type === 'password' ? 'password' : 'text',
        trim: type === 'password' ? false : true,
        value: '',
      },
      button: {
        text: ('login.authentication.' + type + '.button.text').toUpperCase(),
        color: 'accent',
        showImg: false,
      },
      verify: {
        method: type,
      },
    };
  }

  backClick(error: string = '') {
    this._router.navigate(['/login'], {
      queryParams: {
        username: this.username,
        requestId: this.requestId,
        type: this.usernameType,
        login_hint: this.username,
        method: this.usernameType,
        ...( error && { error } || {})
      },
    });
  }

  checkNotificationStatus(status: string) {
    if (status == 'REJECTED_BY_USER') {
      this.errorMessage = 'AUTHENTICATED_REQUEST_DENIED';
      this.backClick(this.errorMessage);
    }
    if (status == 'AUTHENTICATION_CANCELLED') {
      this.errorMessage = 'AUTHENTICATED_REQUEST_DENIED';
      this.backClick(this.errorMessage);
    }
  }

  ngOnDestroy(): void {
    this.$composible.unsubscribe();
  }

    _onClickRegisterGLO = (e): void => {
        if (!e){
            return;
        }
        this._router.navigate(['login'], {
            queryParams: {
                userIdHint: e?.username,
                requestId: this.requestId,
                gloTypeRegister: 'create'
            },
        });

        if (e instanceof KeyboardEvent) {
            // @ts-ignore
            document.querySelector('a').focus()
        }
    }

    changeEmail() {
        this._router.navigate(['login'], {
            queryParams: {
                requestId: this.requestId,
            },
        });
    }
}
